import { useEffect, useEffectEvent, useState } from "react";
import type { ApiClient } from "../../api/client";
import type { Channel, LineRequest, Menu, MenuRules, ModifierGroup, Product } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { eurosToCents, formatEuro } from "../../lib/money";
import { Dialog } from "../../ui/Dialog";
import {
  chooseVariant,
  coveredByAllVegetables,
  isComplete,
  itemsFor,
  maximumOf,
  missingGroups,
  newSelection,
  offersAllVegetables,
  optionPrice,
  sectionsOf,
  selectionFromLine,
  tapWithout,
  toggleAllVegetables,
  toggleChoice,
  toggleOnly,
  toggleOnlyMode,
  toLine,
  type Selection,
} from "./product-options";
import { priceOf } from "../promotions/promotions";
import { useQuote } from "./useQuote";

interface Props {
  api: ApiClient;
  menu: Menu;
  rules: MenuRules;
  product: Product;
  channel: Channel;
  /** Editing an existing ticket line instead of adding a new one. */
  editing?: LineRequest;
  /** The size it starts with: of the tile or the code ("PEPM"), else the product's default size. */
  variant?: string;
  /** The quantity typed with the code ("2DP"). */
  quantity?: number;
  onDone: (line: LineRequest) => void;
  onClose: () => void;
}

/**
 * Size, options, "Zonder" and instructions for one product, in the order of the prototype
 * (buildGroups); the price at the bottom comes from the server.
 */
export function ProductDialog({
  api,
  menu,
  rules,
  product,
  channel,
  editing,
  variant,
  quantity,
  onDone,
  onClose,
}: Props) {
  const [selection, setSelection] = useState<Selection>(() =>
    editing ? selectionFromLine(product, editing) : newSelection(product, variant, quantity),
  );
  const complete = isComplete(menu, product, selection);
  const line = toLine(product, selection);
  const { promotions } = useTill();
  const priced = useQuote(api, complete ? { channel, lines: [line] } : null, JSON.stringify(promotions));
  const missing = missingGroups(menu, product, selection);
  const ingredients = product.ingredients ?? [];

  const submit = () => {
    if (complete) onDone(line);
  };

  // Enter adds the product, also right after tapping an option (focus is then on that option)
  const onEnter = useEffectEvent((event: KeyboardEvent) => {
    if (event.key !== "Enter" || event.isComposing) return;
    event.preventDefault();
    submit();
  });
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      onEnter(event);
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, []);

  /** "2 gratis, daarna € 0,50" or "verplicht" next to the title of a group (prototype). */
  const groupTag = (group: ModifierGroup) => {
    // a product can give more or fewer free picks than the group's default (Menu & prijzen)
    const freeQuantity = product.freeQuantities?.[group.id] ?? group.freeQuantity;
    if (freeQuantity > 0) {
      const first = group.items[0];
      const price = first ? optionPrice(group, first, selection.variant) : null;
      return <em>{t.product.freeThen(freeQuantity, formatEuro(price ?? 0))}</em>;
    }
    if (group.minimum > 0) return <em className={missing.includes(group) ? "need" : ""}>{t.product.required}</em>;
    return null;
  };

  const groupChips = (group: ModifierGroup) => {
    const picked = selection.choices[group.id] ?? [];
    const full = picked.length >= maximumOf(group);
    const freeQuantity = product.freeQuantities?.[group.id] ?? group.freeQuantity;
    return itemsFor(group, channel).map((item) => {
      const price = optionPrice(group, item, selection.variant);
      const on = picked.includes(item.productId);
      // the first picks of a group like "2 gratis sauzen" cost nothing
      const position = on ? picked.indexOf(item.productId) : picked.length;
      const label =
        price === null
          ? ""
          : freeQuantity > 0
            ? position < freeQuantity
              ? t.sale.freeLabel
              : formatEuro(price)
            : price > 0
              ? formatEuro(price)
              : "";
      return (
        <button
          key={item.productId}
          type="button"
          className="chip"
          aria-pressed={on}
          disabled={price === null || (full && !on && group.selectionMode === "MULTIPLE")}
          onClick={() => {
            setSelection(toggleChoice(selection, group, item.productId));
          }}
        >
          {menu.products[item.productId]?.name ?? item.productId}
          {label !== "" && <small className="num">{label}</small>}
        </button>
      );
    });
  };

  const withoutChips = () => {
    // "Enkel" on: a tap keeps the ingredient, everything not tapped stays away
    if (selection.onlyMode) {
      return ingredients.map((ingredient) => (
        <button
          key={ingredient}
          type="button"
          className="chip only"
          aria-pressed={selection.only.includes(ingredient)}
          onClick={() => {
            setSelection(toggleOnly(selection, ingredient));
          }}
        >
          {ingredient}
        </button>
      ));
    }
    const allOn = selection.without.includes(rules.allVegetables);
    return (
      <>
        {/* "Groenten" first: one tap takes every vegetable away (on the receipt "Zonder groenten") */}
        {offersAllVegetables(product, rules) && (
          <button
            type="button"
            className="chip minus zall"
            aria-pressed={allOn}
            onClick={() => {
              setSelection(toggleAllVegetables(selection, rules));
            }}
          >
            {rules.allVegetables}
          </button>
        )}
        {ingredients.map((ingredient) => {
          // with "Groenten" on: struck through, "weg"; a tap means "alles behalve" this one
          const covered = coveredByAllVegetables(selection, product, rules, ingredient);
          return (
            <button
              key={ingredient}
              type="button"
              className={`chip minus${covered ? " zoff" : ""}`}
              aria-pressed={selection.without.includes(ingredient)}
              title={covered ? t.product.allBut(ingredient) : undefined}
              onClick={() => {
                setSelection(tapWithout(selection, product, rules, ingredient));
              }}
            >
              {ingredient}
              {covered && <small>{t.product.gone}</small>}
            </button>
          );
        })}
      </>
    );
  };

  return (
    <Dialog
      title={product.name}
      subtitle={ingredients.length > 0 ? ingredients.join(" · ") : undefined}
      size="product"
      onClose={onClose}
      footer={
        <form
          className="product-foot"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <div className="qty">
            <button
              type="button"
              aria-label="Eén minder"
              disabled={selection.quantity <= 1}
              onClick={() => {
                setSelection({ ...selection, quantity: selection.quantity - 1 });
              }}
            >
              −
            </button>
            <output aria-label="Aantal">{selection.quantity}</output>
            <button
              type="button"
              aria-label="Eén meer"
              onClick={() => {
                setSelection({ ...selection, quantity: Math.min(999, selection.quantity + 1) });
              }}
            >
              +
            </button>
          </div>
          <input
            value={selection.note}
            maxLength={300}
            placeholder={t.product.kitchenNote}
            onChange={(event) => {
              setSelection({ ...selection, note: event.target.value });
            }}
          />
          <span className={`ptotal num ${priced.fresh ? "" : "stale"}`} aria-live="polite">
            {priced.fresh && priced.quote ? formatEuro(priced.quote.totalCents) : "…"}
          </span>
          <button type="submit" className="btn primary" disabled={!complete} data-autofocus>
            {editing ? t.product.update : t.product.add} <kbd>Enter</kbd>
          </button>
        </form>
      }
    >
      {product.variants.length > 1 && (
        <div className="grp">
          <h3>
            {t.product.size}
            {selection.variant === undefined && <em className="need">{t.product.choose(1)}</em>}
          </h3>
          <div className="chips">
            {product.variants.map((item) => (
              <button
                key={item.label}
                type="button"
                className="chip"
                aria-pressed={selection.variant === item.label}
                onClick={() => {
                  setSelection(chooseVariant(menu, product, selection, item.label));
                }}
              >
                {item.label}{" "}
                <small className="num">
                  {formatEuro(eurosToCents(priceOf(promotions, channel, product, item.label)))}
                </small>
              </button>
            ))}
          </div>
        </div>
      )}

      {sectionsOf(menu, product).map((section) =>
        section.kind === "without" ? (
          <div className="grp" key="without">
            <h3>
              {t.product.without}
              <OnlyButton selection={selection} onToggle={setSelection} />
              {selection.onlyMode && selection.only.length === 0 && <em>{t.product.onlyHint}</em>}
            </h3>
            <div className="chips">{withoutChips()}</div>
          </div>
        ) : (
          <div className="grp" key={section.group.id}>
            <h3>
              {section.group.title}
              {groupTag(section.group)}
            </h3>
            <div className="chips">{groupChips(section.group)}</div>
          </div>
        ),
      )}

      {priced.error && <p className="error-text">{priced.error.message}</p>}
    </Dialog>
  );
}

/** The small "Enkel" button next to "Zonder", on the till as in the Tafo app. */
export function OnlyButton({ selection, onToggle }: { selection: Selection; onToggle: (next: Selection) => void }) {
  return (
    <button
      type="button"
      className="onlybtn"
      aria-pressed={selection.onlyMode}
      title={t.product.onlyTitle}
      onClick={() => {
        onToggle(toggleOnlyMode(selection));
      }}
    >
      {t.product.only}
    </button>
  );
}
